import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, ChevronRight, ScanLine, Sparkles, X } from 'lucide-react';
import aiService from '../services/aiService';

const AIChatModal = ({ isOpen, onClose, recommendations = [], currentLanguage = 'zh-TW', onSelectPlace, selectedPlace = null, screen = null, onPickForMe, pickReady = false, onPickHandled }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const requestVersion = useRef(0);
  const contextKey = aiService.buildContext(recommendations, selectedPlace, screen);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const bodyRef = useRef(null);
  const chinese = currentLanguage === 'zh-TW';

  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = messages.length || loading ? body.scrollHeight : 0;
  }, [messages, loading]);

  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
    const onKeyDown = event => event.key === 'Escape' && onCloseRef.current();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    requestVersion.current++;
    aiService.clearConversationHistory();
    setMessages([]); setInput(''); setLoading(false);
    return () => { requestVersion.current++; };
  }, [contextKey, currentLanguage, isOpen]);

  const ask = useCallback(async (prompt, choose = false) => {
    const value = (prompt || input).trim();
    if (!value || loading || screen?.loading) return;
    const version = requestVersion.current;
    setInput('');
    setMessages(current => [...current, { role: 'user', text: value }]);
    setLoading(true);
    try {
      const answer = await aiService.getAIResponse(value, recommendations, null, selectedPlace, currentLanguage, screen, choose);
      if (version !== requestVersion.current) return;
      setMessages(current => [...current, { role: 'assistant', text: answer.text, selectedPlaceId: answer.selectedPlaceId }]);
    } catch (error) {
      if (version !== requestVersion.current) return;
      const fallback = (!screen || choose) && recommendations.find(place => place.opening_hours?.open_now !== false && place.scheduleInfo?.status !== 'late');
      setMessages(current => [...current, { role: 'assistant', text: screen && !choose ? (chinese ? 'AI 暫時無法回答，請稍後重試。' : 'AI is unavailable. Please try again.') : chinese ? 'AI 暫時無法完成比較。可以先查看清單首選，特殊需求仍請向店家確認。' : 'AI comparison is unavailable. You can view the top eligible pick; confirm special requirements with the restaurant.', selectedPlaceId: fallback?.place_id || null }]);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [chinese, currentLanguage, input, loading, recommendations, screen, selectedPlace]);

  useEffect(() => {
    if (!isOpen || !pickReady) return;
    onPickHandled?.();
    if (recommendations.length) ask(chinese ? '直接替我選一間' : 'Pick one for me', true);
    else setMessages([{ role: 'assistant', text: chinese ? '目前沒有符合條件且營業中的店家，試著放寬步行範圍或預算。' : 'No matching open places. Try a wider range or budget.' }]);
  }, [ask, chinese, contextKey, isOpen, onPickHandled, pickReady, recommendations.length]);

  const pick = async () => {
    if (loading || screen?.loading) return;
    try {
      if (!await onPickForMe()) setMessages(current => [...current, { role: 'assistant', text: !screen?.locationReady ? (chinese ? '先在主頁設定探索位置，再按這裡，我就能幫你找附近的店家。' : 'Set a search location on the main page, then try again.') : (chinese ? '搜尋未完成，請確認主頁提示後再試一次。' : 'Search did not finish. Check the page notice and try again.') }]);
    } catch {
      setMessages(current => [...current, { role: 'assistant', text: chinese ? '搜尋暫時無法完成，請再試一次。' : 'Search is unavailable. Please try again.' }]);
    }
  };

  if (!isOpen) return null;
  const prompts = screen ? (chinese ? (selectedPlace ? ['這間店有哪些資訊？', '營業時間是什麼？', '和其他候選比起來如何？'] : recommendations.length ? ['比較畫面上的餐廳', '哪間最省時間？', '這些條件怎麼調整？'] : ['這個頁面怎麼使用？', '照片找餐廳怎麼用？', '收藏怎麼用？']) : ['Explain this screen', 'Compare visible places', 'What should I do next?']) : chinese ? ['直接替我選一間', '比較前兩名', '哪間最省時間？'] : ['Pick one for me', 'Compare the top two', 'Which saves the most time?'];

  return (
    <div className={screen ? `assistant-dock ${selectedPlace ? 'has-selected-place' : ''}` : "assistant-backdrop"} role="dialog" aria-modal={screen ? undefined : true} aria-labelledby="assistant-title">
      <section className="assistant-panel">
        <header className="assistant-header">
          <div><span className="assistant-icon"><img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" /></span><div><h2 id="assistant-title">{screen ? (chinese ? '吃哪間' : 'Foodie') : (chinese ? '幫我選' : 'Pick for me')}<span className="assistant-ai-badge">AI</span></h2><p>{chinese ? '你的選餐小幫手' : 'Your dining companion'}</p></div></div>
          <button ref={closeRef} className="icon-button" onClick={onClose} aria-label={chinese ? '關閉 AI 聊天' : 'Close AI chat'}><X aria-hidden="true" /></button>
        </header>
        <div ref={bodyRef} className="assistant-body" aria-live="polite">
          <div className="assistant-context"><ScanLine aria-hidden="true" /><span>{screen ? (chinese ? `正在看：${screen.title}` : `Viewing: ${screen.title}`) : (chinese ? '目前的候選餐廳' : 'Current candidates')}</span></div>
          {onPickForMe && <button className="surprise-button assistant-pick" disabled={loading || screen?.loading || pickReady} onClick={pick}><Sparkles aria-hidden="true" />{screen?.loading ? (chinese ? '正在找附近店家…' : 'Finding nearby places…') : (chinese ? '都可以，直接幫我選' : 'Anything — pick for me')}</button>}
          {!messages.length && <div className="assistant-intro"><span className="assistant-greeting"><Sparkles aria-hidden="true" />{chinese ? '一起選好這一餐' : 'Find your next bite'}</span><h3>{chinese ? '有點難選？交給我。' : 'Need help choosing?'}</h3><p>{chinese ? '比較眼前的選擇，或問問正在看的店家。' : 'Compare your options or ask about this restaurant.'}</p><div className="prompt-list">{prompts.map(prompt => <button key={prompt} disabled={loading || screen?.loading} onClick={() => ask(prompt)}><span>{prompt}</span><ChevronRight aria-hidden="true" /></button>)}</div></div>}
          {messages.map((message, index) => <div key={index} className={`chat-message ${message.role}`}><span className="chat-speaker">{message.role === 'assistant' ? '吃哪間 AI' : (chinese ? '你' : 'You')}</span>{message.text}{message.selectedPlaceId && <button className="secondary-button assistant-choice" onClick={() => {
            const place = recommendations.find(item => item.place_id === message.selectedPlaceId) || (selectedPlace?.place_id === message.selectedPlaceId ? selectedPlace : null);
            if (place) { onClose(); onSelectPlace(place); }
          }}>{chinese ? '查看這間餐廳' : 'View this restaurant'}<ChevronRight aria-hidden="true" /></button>}</div>)}
          {loading && <div className="chat-message assistant chat-typing" role="status"><span>{chinese ? '正在回答' : 'Thinking'}</span><span className="typing-dots" aria-hidden="true"><i /><i /><i /></span></div>}
        </div>
        <form className="assistant-composer" onSubmit={event => { event.preventDefault(); ask(); }}>
          <label className="sr-only" htmlFor="assistant-input">{chinese ? '輸入問題' : 'Ask a question'}</label>
          <input maxLength={500} id="assistant-input" value={input} onChange={event => setInput(event.target.value)} placeholder={chinese ? '例如：哪間適合現在去？' : 'Which one should I visit now?'} />
          <button type="submit" disabled={!input.trim() || loading || screen?.loading} aria-label={chinese ? '送出問題' : 'Send'}><ArrowUp aria-hidden="true" /></button>
          <p className="assistant-hint">{chinese ? '依目前畫面回答 · 店家資訊請再次確認' : 'Based on this screen · Confirm details with the restaurant'}</p>
        </form>
      </section>
    </div>
  );
};

export default AIChatModal;
